import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, Search, Users, Loader2, ChevronLeft, ChevronRight, ArrowLeft, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/Sheet';
import { Logo } from '@/components/Logo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { useInvestors, useCreateInvestor, useUpdateInvestor } from '@/hooks/useInvestors';
import { useInvestorRateConflicts } from '@/hooks/useInvestorRateConflicts';
import { InvestorForm } from './InvestorForm';
import type { Investor } from '@/lib/types';
import { useAuth } from '@/lib/auth';

type StatusFilter = 'all' | 'active' | 'inactive';

export default function InvestorsPage() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const isRtl = i18n.language === 'ar' || i18n.language === 'ur';

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Investor | null>(null);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const params = useMemo(
    () => ({
      search: search || undefined,
      status: statusFilter === 'all' ? undefined : statusFilter,
      page,
      pageSize: 25,
    }),
    [search, statusFilter, page]
  );

  const { data, isLoading, isError, error, refetch } = useInvestors(params);
  const createMutation = useCreateInvestor();
  const updateMutation = useUpdateInvestor();

  const investors = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  // PR-MAY15-INVESTOR-RATES-DRAWER item 3 — deep-link landing. The trip
  // create flow returns MISSING_INVESTOR_RATE with an action_url like
  // /investors?openId=<uuid>&section=vehicles-rates&vehicleId=<uuid>&siteContractId=<uuid>.
  // We auto-open the drawer for the matching investor. The
  // InvestorVehicleRates panel reads the rest of the params off the
  // URL itself so it can pre-fill the add-rate form.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const openId = searchParams.get('openId');
    if (!openId || editing) return;
    const match = investors.find((i) => i.id === openId);
    if (match) {
      setEditing(match);
      // Drop ONLY the openId from the URL so a refresh doesn't re-open
      // the drawer, but keep the section / vehicleId / siteContractId
      // for the panel to consume.
      const next = new URLSearchParams(searchParams);
      next.delete('openId');
      setSearchParams(next, { replace: true });
    }
  }, [investors, searchParams, editing, setSearchParams]);

  const handleCreate = async (input: Parameters<typeof createMutation.mutateAsync>[0]) => {
    await createMutation.mutateAsync(input);
    setCreating(false);
  };

  const handleUpdate = async (input: Parameters<typeof updateMutation.mutateAsync>[0]['input']) => {
    if (!editing) return;
    await updateMutation.mutateAsync({ id: editing.id, input });
    setEditing(null);
  };

  const formatDate = (s: string | null) => {
    if (!s) return '—';
    try {
      return new Date(s).toLocaleDateString(i18n.language, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return s;
    }
  };

  const displayName = user?.fullNameEn ?? user?.fullName ?? '';

  return (
    <div className="min-h-screen w-full bg-background flex flex-col">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <Logo size={36} variant="mark" />
              <div className="hidden sm:block">
                <div className="font-display text-lg leading-none text-gradient-gold">JASR HUB</div>
                <div className="text-[11px] text-muted-foreground">{t('app.tagline')}</div>
              </div>
            </Link>
          </div>

          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <ThemeToggle />
            <div className="ms-2 ps-2 border-s border-border">
              <Button variant="ghost" size="sm" onClick={logout}>
                {t('auth.logout')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-8">
        {/* Page header */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-2"
            >
              <ArrowLeft className={`h-3 w-3 ${isRtl ? 'rotate-180' : ''}`} />
              {t('common.backToDashboard')}
            </Link>
            <div className="flex items-center gap-3">
              <Users className="h-7 w-7 text-gold" />
              <h1 className="font-display text-3xl text-foreground">
                {t('dashboard.modules.investors')}
              </h1>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {t('investors.subtitle')}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <ConflictBadge />
            <Button variant="gold" size="lg" onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" />
              {t('investors.addInvestor')}
            </Button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="bg-card border border-border rounded-lg p-4 mb-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('investors.searchPlaceholder')}
              className="ps-10"
            />
          </div>
          <div className="flex items-center gap-2">
            {(['all', 'active', 'inactive'] as StatusFilter[]).map((s) => (
              <button
                key={s}
                onClick={() => { setStatusFilter(s); setPage(1); }}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  statusFilter === s
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-secondary'
                }`}
              >
                {t(`investors.filter.${s}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          {isLoading ? (
            <div className="py-16 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : isError ? (
            <div className="py-16 text-center">
              <p className="text-destructive">{(error as Error)?.message ?? 'Error'}</p>
              <Button variant="outline" onClick={() => refetch()} className="mt-4">
                {t('common.retry')}
              </Button>
            </div>
          ) : investors.length === 0 ? (
            <div className="py-20 text-center">
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-30" />
              <h3 className="font-display text-xl text-foreground">
                {search || statusFilter !== 'all'
                  ? t('investors.noResults')
                  : t('investors.empty')}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                {search || statusFilter !== 'all'
                  ? t('investors.tryDifferentSearch')
                  : t('investors.emptyDescription')}
              </p>
              {!search && statusFilter === 'all' && (
                <Button variant="gold" onClick={() => setCreating(true)} className="mt-6">
                  <Plus className="h-4 w-4" />
                  {t('investors.addFirstInvestor')}
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr className="text-start">
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">
                        {t('investors.column.code')}
                      </th>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">
                        {t('investors.column.name')}
                      </th>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">
                        {t('investors.column.phone')}
                      </th>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">
                        {t('investors.column.status')}
                      </th>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">
                        {t('investors.column.joinedDate')}
                      </th>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">
                        {t('investors.column.bankName')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {investors.map((inv) => (
                      <tr
                        key={inv.id}
                        onClick={() => setEditing(inv)}
                        className="border-b border-border last:border-b-0 hover:bg-muted/40 transition-colors cursor-pointer"
                      >
                        <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                          {inv.code}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-foreground">{inv.fullNameEn}</div>
                          {inv.fullNameAr && (
                            <div className="text-xs text-muted-foreground font-arabic" dir="rtl">
                              {inv.fullNameAr}
                            </div>
                          )}
                          {!inv.fullNameAr && inv.fullNameUr && (
                            <div className="text-xs text-muted-foreground font-urdu" dir="rtl">
                              {inv.fullNameUr}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 font-mono text-sm" dir="ltr">
                          {inv.phone}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={inv.status === 'active' ? 'active' : 'inactive'}>
                            {t(`investors.status.${inv.status}`)}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{formatDate(inv.joinedDate)}</td>
                        <td className="px-4 py-3 text-muted-foreground">{inv.bankName ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="px-4 py-3 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3 bg-muted/20">
                <p className="text-sm text-muted-foreground">
                  {t('investors.showingCount', {
                    from: (page - 1) * 25 + 1,
                    to: Math.min(page * 25, total),
                    total,
                  })}
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className={`h-4 w-4 ${isRtl ? 'rotate-180' : ''}`} />
                  </Button>
                  <span className="px-3 text-sm tabular-nums">
                    {page} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                  >
                    <ChevronRight className={`h-4 w-4 ${isRtl ? 'rotate-180' : ''}`} />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Hello row */}
        <p className="text-center text-xs text-muted-foreground mt-8">
          {displayName} · {t(`dashboard.role.${user?.role ?? ''}`, { defaultValue: user?.role ?? '' })}
        </p>
      </main>

      {/* Create drawer */}
      <Sheet open={creating} onOpenChange={setCreating}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{t('investors.addInvestor')}</SheetTitle>
            <SheetDescription>{t('investors.addInvestorDescription')}</SheetDescription>
          </SheetHeader>
          <InvestorForm
            onSubmit={handleCreate}
            onCancel={() => setCreating(false)}
            submitLabel={t('investors.create')}
          />
        </SheetContent>
      </Sheet>

      {/* Edit drawer */}
      <Sheet open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{editing?.fullNameEn ?? ''}</SheetTitle>
            <SheetDescription className="font-mono text-xs">{editing?.code}</SheetDescription>
          </SheetHeader>
          <InvestorForm
            initial={editing}
            onSubmit={handleUpdate}
            onCancel={() => setEditing(null)}
            submitLabel={t('common.save')}
          />
        </SheetContent>
      </Sheet>
    </div>
  );
}

// PR-MAY16-INVESTOR-RATES-V2 item 5 — pending conflict count badge.
// Only renders when there's at least one open conflict; otherwise the
// header stays uncluttered. Wraps the conflicts page link.
function ConflictBadge() {
  const { t } = useTranslation();
  const { data } = useInvestorRateConflicts();
  const count = data?.length ?? 0;
  if (count === 0) return null;
  return (
    <Link
      to="/dashboard/investor-rate-conflicts"
      className="inline-flex items-center gap-1.5 h-11 px-3 rounded-md text-sm font-medium bg-amber-500/10 border border-amber-500/40 text-amber-700 dark:text-amber-400 hover:bg-amber-500/15"
      data-rate-conflicts-badge
    >
      <ShieldAlert className="h-4 w-4" />
      {t('investors.conflicts.badge', { count })}
    </Link>
  );
}
